import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Send,
  ShieldCheck
} from 'lucide-react';
import { countries, popularCountries } from '../data/consultationData';
import { careWorkforceService } from '../config/careWorkforceApi';

/**
 * ── INTAKE SWITCH ──────────────────────────────────────────────────────────
 *
 * `true`  — the form accepts submissions.
 * `false` — the form is replaced by a "we are not taking applications right
 *           now" page, and the nav badge reads "Closed" instead of "New".
 *
 * Flip this one value to open or close a cohort. Nothing else needs changing;
 * the landing page and the header both read it from here.
 */
export const AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN = true;

const radioOptions = {
  yesNo: ['Yes', 'No'],
  yesNoNotSure: ['Yes', 'No', 'Not sure'],
  availability: ['Immediately', 'Within 1 month', 'Within 3 months', 'Later than 3 months'],
  english: ['Basic', 'Intermediate', 'Good', 'Very Good', 'Fluent'],
  experienceYears: ['No experience', 'Less than 1 year', '1-2 years', '3-5 years', '5+ years'],
  education: [
    'Secondary school',
    'Diploma / Certificate',
    "Bachelor's degree",
    "Master's degree",
    'Doctorate',
    'Other'
  ],
  gender: ['Male', 'Female', 'Non-binary', 'Prefer not to say'],
  englishTests: ['IELTS', 'TOEFL', 'PTE', 'OET', 'Duolingo English Test', 'Cambridge English', 'None']
};

const workExperienceOptions = [
  'Elderly care',
  'Disability support',
  'Hospital support work',
  'Home care',
  'Personal care assistance',
  'None'
];

const declarationOptions = [
  {
    key: 'understandNoGuarantee',
    label: 'I understand that submitting this form does not guarantee a job offer or placement'
  },
  {
    key: 'understandSelection',
    label: 'I understand that candidate selection is subject to screening, training, assessment, and employer requirements'
  },
  {
    key: 'confirmAccuracy',
    label: 'I confirm that the information I have provided is accurate to the best of my knowledge'
  }
];

const initialFormData = {
  fullName: '',
  email: '',
  phone: '',
  gender: '',
  dateOfBirth: '',
  nationality: '',
  countryOfResidence: '',
  highestEducation: '',
  fieldOfStudy: '',
  hasHealthcareEducation: '',
  healthcareQualification: '',
  workExperienceAreas: [],
  totalExperience: '',
  spokenEnglish: '',
  writtenEnglish: '',
  takenEnglishTest: '',
  englishTestType: '',
  hasValidPassport: '',
  canObtainPoliceClearance: '',
  willingMedicalChecks: '',
  previousVisaApplication: '',
  visaApplicationDetails: '',
  availability: '',
  companySource: '',
  understandNoGuarantee: false,
  understandSelection: false,
  confirmAccuracy: false,
  consent: false
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.55 },
  viewport: { once: true }
};

const fieldClassName = (hasError) =>
  `w-full rounded-xl border px-4 py-3 text-sm md:text-base bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;

const SectionCard = ({ number, title, children }) => (
  <section className="rounded-[28px] border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center font-semibold">
        {number}
      </div>
      <h2 className="text-2xl md:text-3xl font-semibold text-primary">{title}</h2>
    </div>
    <div className="space-y-6">{children}</div>
  </section>
);

const Field = ({ label, required, error, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-800 mb-2">
      {label} {required ? <span className="text-red-500">*</span> : null}
    </label>
    {children}
    {hint ? <p className="text-xs text-gray-500 mt-2">{hint}</p> : null}
    {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
  </div>
);

const RadioGroup = ({ name, value, options, onChange }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {options.map((option) => (
      <label
        key={option}
        className={`flex items-center gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-colors ${
          value === option ? 'border-secondary bg-surface-tint' : 'border-gray-200 bg-white hover:bg-gray-50'
        }`}
      >
        <input
          type="radio"
          name={name}
          value={option}
          checked={value === option}
          onChange={onChange}
          className="text-secondary focus:ring-secondary"
        />
        <span className="text-sm text-gray-700">{option}</span>
      </label>
    ))}
  </div>
);

const AustraliaCareWorkforceIntakePage = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [isEmailPrepopulated, setIsEmailPrepopulated] = useState(false);

  // Read URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const name = urlParams.get('name');
    const email = urlParams.get('email');
    const src = urlParams.get('src');

    if (name || email || src) {
      setFormData(prev => ({
        ...prev,
        ...(name && { fullName: decodeURIComponent(name) }),
        ...(email && { email: decodeURIComponent(email) }),
        ...(src && { companySource: src })
      }));

      // Mark email as prepopulated to make it read-only
      if (email) {
        setIsEmailPrepopulated(true);
      }
    }
  }, []);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [pendingFormData, setPendingFormData] = useState(null);

  const countryOptions = useMemo(() => {
    const popularCodes = new Set(popularCountries.map((country) => country.code));
    const rest = countries.filter((country) => !popularCodes.has(country.code));
    return [...popularCountries, ...rest];
  }, []);

  if (!AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN) {
    return (
      <div className="bg-surface-muted min-h-screen">
        <section className="bg-gradient-to-br from-primary to-secondary text-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
            <motion.div {...fadeInUp} className="max-w-4xl">
              <span className="inline-flex items-center rounded-full bg-white/10 border border-white/15 text-xs font-medium px-4 py-2 mb-6">
                Australia Care Workforce Candidate Program
              </span>
              <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6">
                Australia Health Care Assistant Intake Form
              </h1>
              <p className="text-white/85 text-base md:text-lg font-light leading-relaxed max-w-3xl">
                The current cohort is no longer taking intake submissions.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <motion.div
              {...fadeInUp}
              className="rounded-[28px] border border-gray-200 bg-white p-8 md:p-12 shadow-sm text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CalendarClock className="text-white" size={32} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-4">
                Intake is closed at present
              </h2>
              <p className="text-gray-600 font-light leading-relaxed mb-3">
                This cohort is now full, so new submissions have been put on hold.
              </p>
              <p className="text-gray-600 font-light leading-relaxed mb-8">
                Once the next cohort opens, applications resume. Do check back for that
                announcement.
              </p>
              <a
                href="/permanent-residency/australia-care-workforce"
                className="bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-xl text-sm md:text-base inline-flex items-center justify-center gap-2 transition-colors"
              >
                Return to Program Details
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleExperienceChange = (option) => {
    setFormData((prev) => {
      const current = prev.workExperienceAreas;
      let next;

      if (option === 'None') {
        next = current.includes('None') ? [] : ['None'];
      } else {
        const withoutNone = current.filter((item) => item !== 'None');
        next = withoutNone.includes(option)
          ? withoutNone.filter((item) => item !== option)
          : [...withoutNone, option];
      }

      return {
        ...prev,
        workExperienceAreas: next
      };
    });

    if (errors.workExperienceAreas) {
      setErrors((prev) => ({
        ...prev,
        workExperienceAreas: ''
      }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    const requiredFields = [
      'fullName',
      'email',
      'phone',
      'gender',
      'dateOfBirth',
      'nationality',
      'countryOfResidence',
      'highestEducation',
      'fieldOfStudy',
      'hasHealthcareEducation',
      'totalExperience',
      'spokenEnglish',
      'writtenEnglish',
      'takenEnglishTest',
      'hasValidPassport',
      'canObtainPoliceClearance',
      'willingMedicalChecks',
      'previousVisaApplication',
      'availability'
    ];

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (typeof value === 'string' && !value.trim()) {
        nextErrors[field] = 'This field is required';
      }
    });

    if (!formData.workExperienceAreas.length) {
      nextErrors.workExperienceAreas = 'Select at least one option';
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (formData.hasHealthcareEducation === 'Yes' && !formData.healthcareQualification.trim()) {
      nextErrors.healthcareQualification = 'Please state your qualification or training';
    }

    if (formData.takenEnglishTest === 'Yes' && !formData.englishTestType.trim()) {
      nextErrors.englishTestType = 'Please select the test type';
    }

    if (formData.previousVisaApplication === 'Yes' && !formData.visaApplicationDetails.trim()) {
      nextErrors.visaApplicationDetails = 'Please provide brief details';
    }

    declarationOptions.forEach(({ key }) => {
      if (!formData[key]) {
        nextErrors[key] = 'Required';
      }
    });

    if (!formData.consent) {
      nextErrors.consent = 'Consent is required before submission';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildSubmissionPayload = () => {
    // Send the complete form data directly to the care workforce API
    return {
      ...formData,
      email: formData.email.trim().toLowerCase(),
      fullName: formData.fullName.trim(),
      phone: formData.phone.trim()
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitResult(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildSubmissionPayload();
      
      // Send verification code to email
      const response = await careWorkforceService.sendVerificationCode({
        email: payload.email,
        fullName: payload.fullName
      });

      if (response.success) {
        // Store form data and show verification modal
        setPendingFormData(payload);
        setShowVerificationModal(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitResult({
          type: 'error',
          message: response.message || 'Failed to send verification code. Please try again.'
        });
      }
    } catch (error) {
      setSubmitResult({
        type: 'error',
        message: error.message || 'We could not send verification code. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerificationError('');

    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    if (!pendingFormData) {
      setVerificationError('Form data not found. Please try submitting again.');
      return;
    }

    setIsVerifying(true);

    try {
      // Verify the OTP code
      const verifyResponse = await careWorkforceService.verifyEmail({
        email: pendingFormData.email,
        verificationCode: verificationCode.trim()
      });

      if (verifyResponse.verified && verifyResponse.verificationId) {
        // Now submit the application with verificationId
        const submitResponse = await careWorkforceService.submitApplication({
          ...pendingFormData,
          verificationId: verifyResponse.verificationId
        });

        if (submitResponse.success) {
          setShowVerificationModal(false);
          setSubmitResult({
            type: 'success',
            message: submitResponse.message || 'Your application is in. We will review it and get in touch with shortlisted candidates.'
          });
          setVerificationCode('');
          setVerificationId('');
          setPendingFormData(null);
          setFormData(initialFormData);
        } else {
          setVerificationError(submitResponse.message || 'Failed to submit application. Please try again.');
        }
      }
    } catch (error) {
      setVerificationError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="bg-surface-muted min-h-screen">
      <section className="bg-gradient-to-br from-primary to-secondary text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <span className="inline-flex items-center rounded-full bg-white/10 border border-white/15 text-xs font-medium px-4 py-2 mb-6">
              Australia Care Workforce Candidate Program
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6">
              Australia Health Care Assistant Intake Form
            </h1>
            <p className="text-white/85 text-base md:text-lg font-light leading-relaxed max-w-3xl">
              Filling this in puts you forward for consideration. It gives us the background needed
              to review you for possible progression through intake; it guarantees neither a
              placement nor a job offer.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {submitResult ? (
            <div
              className={`mb-8 rounded-2xl border px-5 py-4 flex items-start gap-3 ${
                submitResult.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-900'
                  : 'bg-red-50 border-red-200 text-red-900'
              }`}
            >
              {submitResult.type === 'success' ? (
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
              )}
              <p className="text-sm md:text-base">{submitResult.message}</p>
            </div>
          ) : null}

          <motion.div {...fadeInUp} className="rounded-[28px] border border-blue-100 bg-blue-50 p-6 md:p-8 mb-8">
            <div className="flex items-start gap-4">
              <ShieldCheck className="text-secondary mt-0.5 flex-shrink-0" size={22} />
              <div>
                <h2 className="text-xl font-semibold text-primary mb-2">Before you send this in</h2>
                <p className="text-gray-700 font-light leading-relaxed">
                  Take care over your answers and keep them truthful. Progression is shaped by
                  employer requirements, assessment, training, screening, and the intake review
                  itself.
                </p>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <SectionCard number="1" title="Personal Information">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Full Name" required error={errors.fullName}>
                  <input name="fullName" value={formData.fullName} onChange={handleInputChange} className={fieldClassName(errors.fullName)} />
                </Field>
                <Field label="Email Address" required error={errors.email}>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    className={fieldClassName(errors.email)} 
                    readOnly={isEmailPrepopulated}
                    style={isEmailPrepopulated ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                  {isEmailPrepopulated && (
                    <p className="text-xs text-gray-500 mt-2">This email address was pre-filled and cannot be edited</p>
                  )}
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <input name="phone" value={formData.phone} onChange={handleInputChange} className={fieldClassName(errors.phone)} />
                </Field>
                <Field label="Gender" required error={errors.gender}>
                  <select name="gender" value={formData.gender} onChange={handleInputChange} className={fieldClassName(errors.gender)}>
                    <option value="">Select gender</option>
                    {radioOptions.gender.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Date of Birth" required error={errors.dateOfBirth}>
                  <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} className={fieldClassName(errors.dateOfBirth)} />
                </Field>
                <Field label="Nationality" required error={errors.nationality}>
                  <select name="nationality" value={formData.nationality} onChange={handleInputChange} className={fieldClassName(errors.nationality)}>
                    <option value="">Select nationality</option>
                    {countryOptions.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Country of Residence" required error={errors.countryOfResidence}>
                  <select name="countryOfResidence" value={formData.countryOfResidence} onChange={handleInputChange} className={fieldClassName(errors.countryOfResidence)}>
                    <option value="">Select country</option>
                    {countryOptions.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </SectionCard>

            <SectionCard number="2" title="Education and Background">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Highest level of education you have completed" required error={errors.highestEducation}>
                  <select name="highestEducation" value={formData.highestEducation} onChange={handleInputChange} className={fieldClassName(errors.highestEducation)}>
                    <option value="">Select education level</option>
                    {radioOptions.education.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Field of Study" required error={errors.fieldOfStudy}>
                  <input name="fieldOfStudy" value={formData.fieldOfStudy} onChange={handleInputChange} className={fieldClassName(errors.fieldOfStudy)} />
                </Field>
              </div>
              <Field label="Have you completed any healthcare-related training or education?" required error={errors.hasHealthcareEducation}>
                <RadioGroup name="hasHealthcareEducation" value={formData.hasHealthcareEducation} options={radioOptions.yesNo} onChange={handleInputChange} />
              </Field>
              {formData.hasHealthcareEducation === 'Yes' ? (
                <Field label="If yes, name the qualification or training" required error={errors.healthcareQualification}>
                  <input name="healthcareQualification" value={formData.healthcareQualification} onChange={handleInputChange} className={fieldClassName(errors.healthcareQualification)} />
                </Field>
              ) : null}
            </SectionCard>

            <SectionCard number="3" title="Work Experience">
              <Field label="Have you worked in any of these areas?" required error={errors.workExperienceAreas} hint="Select as many as apply">
                <div className="grid md:grid-cols-2 gap-3">
                  {workExperienceOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-colors ${
                        formData.workExperienceAreas.includes(option)
                          ? 'border-secondary bg-surface-tint'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.workExperienceAreas.includes(option)}
                        onChange={() => handleExperienceChange(option)}
                        className="rounded border-gray-300 text-secondary focus:ring-secondary"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.workExperienceAreas ? <p className="text-sm text-red-600 mt-2">{errors.workExperienceAreas}</p> : null}
              </Field>
              <Field label="Years of relevant care-related experience in total" required error={errors.totalExperience}>
                <select name="totalExperience" value={formData.totalExperience} onChange={handleInputChange} className={fieldClassName(errors.totalExperience)}>
                  <option value="">Select experience range</option>
                  {radioOptions.experienceYears.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Field>
            </SectionCard>

            <SectionCard number="4" title="English and Communication">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="How do you rate your spoken English?" required error={errors.spokenEnglish}>
                  <select name="spokenEnglish" value={formData.spokenEnglish} onChange={handleInputChange} className={fieldClassName(errors.spokenEnglish)}>
                    <option value="">Select level</option>
                    {radioOptions.english.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="How do you rate your written English?" required error={errors.writtenEnglish}>
                  <select name="writtenEnglish" value={formData.writtenEnglish} onChange={handleInputChange} className={fieldClassName(errors.writtenEnglish)}>
                    <option value="">Select level</option>
                    {radioOptions.english.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Have you sat an English proficiency test before?" required error={errors.takenEnglishTest}>
                <RadioGroup name="takenEnglishTest" value={formData.takenEnglishTest} options={radioOptions.yesNo} onChange={handleInputChange} />
              </Field>
              {formData.takenEnglishTest === 'Yes' ? (
                <Field label="Which test was it?" required error={errors.englishTestType}>
                  <select name="englishTestType" value={formData.englishTestType} onChange={handleInputChange} className={fieldClassName(errors.englishTestType)}>
                    <option value="">Select test type</option>
                    {radioOptions.englishTests.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
            </SectionCard>

            <SectionCard number="5" title="Documentation and Travel Readiness">
              <Field label="Do you hold a valid international passport at present?" required error={errors.hasValidPassport}>
                <RadioGroup name="hasValidPassport" value={formData.hasValidPassport} options={['Yes', 'No', 'Applied, not yet received']} onChange={handleInputChange} />
              </Field>
              <Field label="Could you obtain a police clearance/certificate if required?" required error={errors.canObtainPoliceClearance}>
                <RadioGroup name="canObtainPoliceClearance" value={formData.canObtainPoliceClearance} options={radioOptions.yesNoNotSure} onChange={handleInputChange} />
              </Field>
              <Field label="Would you undergo medical checks if the process requires them?" required error={errors.willingMedicalChecks}>
                <RadioGroup name="willingMedicalChecks" value={formData.willingMedicalChecks} options={radioOptions.yesNo} onChange={handleInputChange} />
              </Field>
              <Field label="Have you applied for a visa before, whether to Australia or elsewhere?" required error={errors.previousVisaApplication}>
                <RadioGroup name="previousVisaApplication" value={formData.previousVisaApplication} options={radioOptions.yesNo} onChange={handleInputChange} />
              </Field>
              {formData.previousVisaApplication === 'Yes' ? (
                <Field label="If yes, share brief details" required error={errors.visaApplicationDetails}>
                  <textarea name="visaApplicationDetails" value={formData.visaApplicationDetails} onChange={handleInputChange} rows="3" className={fieldClassName(errors.visaApplicationDetails)} />
                </Field>
              ) : null}
            </SectionCard>

            <SectionCard number="6" title="Availability">
              <Field label="If shortlisted, how soon could you begin screening/training?" required error={errors.availability}>
                <RadioGroup name="availability" value={formData.availability} options={radioOptions.availability} onChange={handleInputChange} />
              </Field>
            </SectionCard>

            <SectionCard number="7" title="Declaration">
              <div className="space-y-4">
                {declarationOptions.map((item) => (
                  <label key={item.key} className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-surface-muted px-4 py-4">
                    <input
                      type="checkbox"
                      name={item.key}
                      checked={formData[item.key]}
                      onChange={handleInputChange}
                      className="mt-1 rounded border-gray-300 text-secondary focus:ring-secondary"
                    />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </label>
                ))}
                {declarationOptions.some((item) => errors[item.key]) ? (
                  <p className="text-sm text-red-600">Please confirm all declaration statements.</p>
                ) : null}
              </div>

              <label className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-blue-50 px-4 py-4">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-secondary focus:ring-secondary"
                />
                <span className="text-sm text-gray-700">
                  I consent to the use of my information for candidate screening, communication,
                  and program evaluation purposes.
                </span>
              </label>
              {errors.consent ? <p className="text-sm text-red-600">{errors.consent}</p> : null}
            </SectionCard>

            <div className="rounded-[28px] border border-primary/10 bg-white p-6 md:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-semibold text-primary mb-2">Send your intake form</h2>
                  <p className="text-gray-600 font-light leading-relaxed max-w-2xl">
                    Your application enters the program review process as soon as it arrives. Where
                    a candidate is shortlisted, we may make contact about the next stage.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary-hover disabled:opacity-70 disabled:cursor-not-allowed text-white px-8 py-4 rounded-xl text-sm md:text-base inline-flex items-center justify-center gap-2 transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Intake Form
                      <Send size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          <motion.div {...fadeInUp} className="mt-8 text-center text-xs text-gray-500">
            Screening and information for the program are the only purposes of this form. Sending
            it guarantees no migration outcome, sponsorship, employment, or placement.
          </motion.div>
        </div>
      </section>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-semibold text-primary mb-2">Verify Your Email</h2>
              <p className="text-gray-600 text-sm">
                A 6-digit verification code is on its way to your email address. Enter it below and your application will be complete.
              </p>
            </div>

            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setVerificationError('');
                  }}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="w-full text-center text-2xl font-mono tracking-widest rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                  autoFocus
                />
                {verificationError && (
                  <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {verificationError}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700">
                <p className="font-medium mb-1">No code yet?</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>• Look in your spam or junk folder</li>
                  <li>• Codes stay valid for 15 minutes</li>
                  <li>• Confirm the email address you entered is right</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowVerificationModal(false);
                    setVerificationCode('');
                    setVerificationError('');
                    setPendingFormData(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isVerifying || verificationCode.length !== 6}
                  className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-colors"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={18} />
                      Verify Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AustraliaCareWorkforceIntakePage;
